import { useCallback, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareExport, rasterize, type ExportSource } from '../export/exportPipeline';
import { defaultSettings, exportBlockers } from '../export/exportModel';

/** Start the write during the user gesture; Safari requires a promised image Blob. */
export function useSystemClipboard(source: RefObject<ExportSource | null>) {
  const { t } = useTranslation();
  const [clipboardMessage, setClipboardMessage] = useState<{ text: string; error: boolean } | null>(null);
  const generation = useRef(0);
  const copyToSystemClipboard = useCallback(() => {
    const snapshot = source.current;
    if (!snapshot) return;
    const request = ++generation.current;
    setClipboardMessage(null);
    const failed = () => {
      if (request === generation.current) setClipboardMessage({ text: t('systemClipboard.failed'), error: true });
    };
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
      failed();
      return;
    }
    const settings = { ...defaultSettings('png'), scope: 'selection' as const };
    const image = prepareExport(snapshot, settings).then((prepared) => {
      if (exportBlockers(settings, prepared.sizeMm, prepared).length || prepared.fontsUnavailable) {
        throw new Error('Selection could not be rendered.');
      }
      return rasterize(prepared.svg, prepared.sizeMm, 300, 'png');
    });
    // A rejected write can leave the promised image unconsumed by the browser.
    void image.catch(() => {});
    try {
      void navigator.clipboard.write([new ClipboardItem({ 'image/png': image })]).then(() => {
        if (request === generation.current) setClipboardMessage({ text: t('systemClipboard.copied'), error: false });
      }, failed);
    } catch {
      failed();
    }
  }, [source, t]);
  return { copyToSystemClipboard, clipboardMessage };
}
